import { TagCommanderService } from 'features/tagCommander/business/TagCommander.service';
import React, { ComponentPropsWithRef } from 'react';
import { useDispatch } from 'react-redux';
import { useAuthModal } from '@auth/business/AuthModal.hook';
import { BasicLink } from '@components/buttons/BasicLink/BasicLink.component';
import { PrimaryButton } from '@components/buttons/FlatButton/FlatButton.component';
import { SpinnerIcon } from '@components/icons/Spinner.icon';
import { Text } from '@components/Text/Text.component';
import { formValidator } from '@form/business/form.types';
import { FormValidators, useFormState } from '@form/business/useFormState.hook';
import { Input } from '@form/components/Input/Input.component';
import { PasswordInput } from '@form/components/PasswordInput/PasswordInput.component';
import { PlcForm } from '@form/components/PlcForm/PlcForm.component';
import { useTranslation } from '@locales/useTranslation.hook';
import { PlcDispatch } from '@store/store';
import { useClassNames } from '@styles/breakpoints';
import { TextStyle } from '@styles/Text.style';
import { ApiStatusCode } from '@utils/api.utils';
import { urlEnum } from '@utils/url';
import classnames from 'classnames';

import './SignInForm.scss';

type Props = Omit<ComponentPropsWithRef<'form'>, 'onSubmit'> & {
  close?: () => void;
  wide?: boolean;
  callbackUrl?: string;
  autoFocus?: boolean;
  isInline?: boolean;
};

type FormState = {
  email: string;
  password: string;
};

const initialFormState: FormState = {
  email: '',
  password: '',
};

function SignInForm({ close, wide, isInline, className, callbackUrl, autoFocus = true, ...rest }: Props) {
  const {
    auth: { signIn },
  } = useDispatch<PlcDispatch>();

  const { showForgottenPasswordModal, showForgottenEmailModal } = useAuthModal();
  const { t } = useTranslation(['auth']);

  /* eslint-disable sort-keys-fix/sort-keys-fix */
  const validators: FormValidators<FormState> = {
    email: {
      // We do not check for email format for former users comming from old pleinchamp to be able to connect
      method: value => [formValidator(value, t, true).required()],
      ref: undefined,
    },
    password: {
      method: value => [formValidator(value, t, true).required()],
      ref: undefined,
    },
  };

  const {
    formData: { email, password },
    setFormField,
    plcFormProps: { scrollToFirstError: _, isFormLoading, ...restFormProps },
    isSubmitted,
    disabledSubmitButton,
    Message,
    setErrorMessage,
  } = useFormState({
    initialFormState,
    validators,
  });

  const onSubmitCallback = () => {
    return signIn({ callbackUrl, email, password })
      .then(() => {
        if (close) {
          close();
        }
      })
      .catch((e: any) => {
        setErrorMessage(
          e.response?.status === ApiStatusCode.unauthorized ? t('auth.sign-in.error') : t('common.error.generic')
        );
      });
  };

  const rootClassname = useClassNames({ all: ['sign-in', 'flex', 'column', className], s: [] });
  const signUpMessageClass = useClassNames({ all: ['flex', 'column'], s: ['row'] });

  return (
    <PlcForm
      className={rootClassname}
      // Only display the loader on the page not in the modal
      isFormLoading={isInline ? isFormLoading : undefined}
      onSubmitCallback={onSubmitCallback}
      {...restFormProps}
      {...rest}
    >
      <Message className="plc-mb-s" />
      <div className={classnames({ wide })}>
        <div className="email-wrapper">
          <Input
            autoFocus={autoFocus}
            className="plc-mb-xs"
            disabled={isFormLoading}
            id="sign-in-email-input"
            isHintVisible={isSubmitted}
            labelFlavour="grey"
            labelI18n="auth.email.label"
            onChange={e => setFormField('email', e.target.value)}
            placeholderI18n="auth.email.placeholder"
            pushContentBelow={true}
            type="email"
            validators={validators.email?.method(email)}
            value={email}
          />
          <div className="plc-mb-basis">
            <BasicLink
              className="forgotten-link tiny neutral"
              isUnderlined
              onClick={showForgottenEmailModal}
              textProps={{ flavour: 'grey', i18nKey: 'auth.forgotten-email', variant: 'tiny' }}
            />
          </div>
        </div>
        <div className="password-wrapper">
          <PasswordInput
            className="plc-mb-xs"
            disabled={isFormLoading}
            id="sign-in-password-input"
            isHintVisible={isSubmitted}
            labelFlavour="grey"
            labelI18n="auth.password.label"
            onChange={e => {
              e.preventDefault();
              setFormField('password', e.target.value);
            }}
            onSubmit={e => e.preventDefault()}
            placeholderI18n="auth.password.placeholder"
            pushContentBelow={true}
            validators={validators.password?.method(password)}
            value={password}
          />

          <div className="plc-mb-l">
            <BasicLink
              className="forgotten-link tiny neutral"
              isUnderlined
              onClick={showForgottenPasswordModal}
              textProps={{ flavour: 'grey', i18nKey: 'auth.forgotten-password', variant: 'tiny' }}
            />
          </div>
        </div>
      </div>

      <PrimaryButton
        className="block inline-block-up-s float-left-up-s plc-mb-m"
        disabled={disabledSubmitButton}
        // Disaled because throw Error: A cross-origin error was thrown. React doesn't have access to the actual error object in development. See https://fb.me/react-crossorigin-error for more information.
        // eslint-disable-next-line react/jsx-no-bind
        onClick={event =>
          TagCommanderService.triggerEvent('click', event.currentTarget, {
            button_name: t('auth.connect'),
          })
        }
        textProps={{ i18nKey: !isInline && isFormLoading ? undefined : 'auth.connect', withArrow: true }}
        type="submit"
      >
        {!isInline && isFormLoading && <SpinnerIcon height={TextStyle.p.lineHeight} width={TextStyle.p.lineHeight} />}
      </PrimaryButton>

      <Text className={signUpMessageClass} i18nKey="auth.not-signed-up" variant="small">
        <BasicLink
          as={urlEnum.signUp}
          className="plc-ml-up-s-xs"
          href={urlEnum.signUp}
          isUnderlined
          onClick={close}
          textProps={{ flavour: 'red', i18nKey: 'auth.sign-up.link' }}
        />
      </Text>
    </PlcForm>
  );
}

export { SignInForm };
